import { createFileRoute, Link } from '@tanstack/react-router'
import { Section, SectionHeader } from '@/components/ui/section'
import { Button } from '@/components/ui/button'
import {
  BookOpen,
  TrendingUp,
  BarChart2,
  Activity,
  ShieldCheck,
  Zap,
  PlayCircle
} from 'lucide-react'

export const Route = createFileRoute('/education')({
  component: EducationComponent,
})

function EducationComponent() {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 px-4 sm:px-6 lg:px-8 border-b border-white/10 bg-grid-white/[0.02]">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center rounded-full border border-blue-500/30 bg-blue-500/10 px-3 py-1 text-sm font-medium text-blue-400 mb-6">
            <BookOpen className="mr-2 h-4 w-4" />
            Academy
          </div>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 bg-gradient-to-br from-white to-white/60 bg-clip-text text-transparent">
            Master the Art of <br />
            <span className="text-blue-500">Futures Trading</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
            Comprehensive guides, heavy-duty strategies, and order flow mastery.
            From novice to algorithmic expert.
          </p>
          <div className="flex justify-center gap-4">
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
              Start Foundation Course
            </Button>
            <Button size="lg" variant="outline">
              View All Modules
            </Button>
          </div>
        </div>
      </section>

      {/* Learning Tracks */}
      <Section className="bg-background">
        <SectionHeader
          title="Learning Tracks"
          description="Structured paths designed to take you from basic concepts to deployment-ready strategies."
        />

        <div className="grid md:grid-cols-3 gap-8 mt-12">
          {/* Track 1: Order Flow */}
          <div className="group relative p-6 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors">
            <div className="h-12 w-12 rounded-lg bg-emerald-500/20 flex items-center justify-center mb-6 text-emerald-500">
              <Activity className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-bold mb-3">Order Flow Mastery</h3>
            <p className="text-muted-foreground mb-6">
              Understand the mechanics of the market. Deep dive into DOM, Footprint charts, and Volume Profile analysis.
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center text-sm text-gray-300">
                <PlayCircle className="mr-2 h-4 w-4 text-emerald-500" />
                Reading the DOM
              </li>
              <li className="flex items-center text-sm text-gray-300">
                <PlayCircle className="mr-2 h-4 w-4 text-emerald-500" />
                Volume Profile Strategies
              </li>
              <li className="flex items-center text-sm text-gray-300">
                <PlayCircle className="mr-2 h-4 w-4 text-emerald-500" />
                Delta & CVD Divergences
              </li>
            </ul>
            <Button variant="link" className="p-0 text-emerald-400 group-hover:text-emerald-300">
              Start Track &rarr;
            </Button>
          </div>

          {/* Track 2: Algorithmic Design */}
          <div className="group relative p-6 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors">
            <div className="h-12 w-12 rounded-lg bg-blue-500/20 flex items-center justify-center mb-6 text-blue-500">
              <Zap className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-bold mb-3">Algorithmic Design</h3>
            <p className="text-muted-foreground mb-6">
              Translate customized trading logic into code. Learn structure, event-loops, and execution handling.
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center text-sm text-gray-300">
                <PlayCircle className="mr-2 h-4 w-4 text-blue-500" />
                Strategy Architecture
              </li>
              <li className="flex items-center text-sm text-gray-300">
                <PlayCircle className="mr-2 h-4 w-4 text-blue-500" />
                Signal Generation
              </li>
              <li className="flex items-center text-sm text-gray-300">
                <PlayCircle className="mr-2 h-4 w-4 text-blue-500" />
                Execution Optimization
              </li>
            </ul>
            <Button variant="link" className="p-0 text-blue-400 group-hover:text-blue-300">
              Start Track &rarr;
            </Button>
          </div>

          {/* Track 3: Risk & Management */}
          <div className="group relative p-6 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 transition-colors">
            <div className="h-12 w-12 rounded-lg bg-purple-500/20 flex items-center justify-center mb-6 text-purple-500">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <h3 className="text-xl font-bold mb-3">Risk Intelligence</h3>
            <p className="text-muted-foreground mb-6">
              The difference between gambling and trading. Learn dynamic position sizing and portfolio correlation.
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center text-sm text-gray-300">
                <PlayCircle className="mr-2 h-4 w-4 text-purple-500" />
                Portfolio Theory
              </li>
              <li className="flex items-center text-sm text-gray-300">
                <PlayCircle className="mr-2 h-4 w-4 text-purple-500" />
                Drawdown Control
              </li>
              <li className="flex items-center text-sm text-gray-300">
                <PlayCircle className="mr-2 h-4 w-4 text-purple-500" />
                Variance Analysis
              </li>
            </ul>
            <Button variant="link" className="p-0 text-purple-400 group-hover:text-purple-300">
              Start Track &rarr;
            </Button>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <section className="py-24 border-t border-white/10">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl font-bold mb-6">Ready to apply what you've learned?</h2>
          <p className="text-muted-foreground mb-10 text-lg">
            Explore our curated list of resources including data feeds, direct market access providers, and charting platforms.
          </p>
          <Link to="/resources">
            <Button size="lg" className="bg-white text-black hover:bg-gray-200">
              Explore Resources
            </Button>
          </Link>
        </div>
      </section>
    </div>
  )
}
